import React, { useState } from 'react';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, Card, CardContent, List, Typography } from '@mui/material';

import { enqueueSuccess } from '@paragon/notification-tools';

import { LoginDataSliceState } from '../../store/logInDataSlice';
import { useAppWithLogInDataSelector } from '../hooks';
import { Plan } from './common';
import { ManagePaymentButton } from './ManagePaymentButton';
import { PlanCardFeatureItem } from './PlanCardFeatureItem';
import { BillingPeriod } from './UpgradeAccount';

export function formatSubscriptionDate(subscriptionDate: string | Date): string {
  const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
  return new Date(subscriptionDate).toLocaleDateString('en-US', options);
}

export interface PlanCardProps {
  readonly plan: Plan;

  readonly billingPeriod: BillingPeriod;
  readonly displayBillingPeriod?: boolean; // default: true

  /**
   * Only applicable if this is the active plan.
   */
  readonly currentPeriodEnd?: Date;

  readonly active?: boolean;
  readonly downgradePending?: boolean;

  readonly canUpgradeToThisPlan: boolean;
  readonly canManagePaymentForThisPlan: boolean;
  readonly upgradeToThisPlan?: () => Promise<void>;
  readonly downgradeToFreePlan?: () => Promise<void>;
  readonly cancelDowngrade?: () => Promise<void>;

  readonly startFreeTrial?: () => Promise<void>;
  readonly freeTrialEndDate?: string;
}

export function PlanCard({
  plan,
  billingPeriod,
  currentPeriodEnd,
  displayBillingPeriod,
  active,
  downgradePending,
  canUpgradeToThisPlan,
  canManagePaymentForThisPlan,
  upgradeToThisPlan,
  downgradeToFreePlan,
  cancelDowngrade,
  startFreeTrial,
  freeTrialEndDate,
}: PlanCardProps) {
  const dispatch = useDispatch<ThunkDispatch<LoginDataSliceState, void, Action>>();

  const ongoingFreeTrial = useAppWithLogInDataSelector((state) => state.logInData.subscription === 'trial');

  const [upgradeOngoing, setUpgradeOngoing] = useState(false);
  const [downgradeOngoing, setDowngradeOngoing] = useState(false);
  const [cancelDowngradeOngoing, setCancelDowngradeOngoing] = useState(false);

  let planActionArea = null;
  if (active && !ongoingFreeTrial) {
    const contactParagonSales = (
      <Button
        variant="outlined"
        onClick={() => void window.open(`mailto:sales@paragontruss.com`, '_blank')}
        sx={{ width: '100%', mt: 2 }}
      >
        Contact Paragon about your plan
      </Button>
    );
    if (downgradePending && cancelDowngrade) {
      planActionArea = (
        <>
          <Alert severity="warning">
            Changing to the Free Plan {currentPeriodEnd?.toLocaleDateString() ?? 'at period end'}
          </Alert>
          <Button
            variant="contained"
            sx={{ backgroundColor: plan.color, color: 'white', width: '100%', mt: 2 }}
            loading={cancelDowngradeOngoing}
            onClick={async () => {
              setCancelDowngradeOngoing(true);
              await cancelDowngrade();
              setCancelDowngradeOngoing(false);
              dispatch(enqueueSuccess(`Success! You will remain on the current subscription.`));
            }}
          >
            Stay on this Subscription
          </Button>
          {contactParagonSales}
        </>
      );
    } else {
      planActionArea = (
        <>
          {currentPeriodEnd && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Plan renews {currentPeriodEnd.toLocaleDateString()}
            </Alert>
          )}
          {canManagePaymentForThisPlan ? <ManagePaymentButton /> : null}
          {(billingPeriod === 'month'
            ? plan.stripePriceLookupKeyMonthly !== null
            : plan.stripePriceLookupKeyYearly !== null) &&
            downgradeToFreePlan && (
              <Button
                variant="outlined"
                sx={{ color: 'white', width: '100%', mt: 2 }}
                loading={downgradeOngoing}
                onClick={async () => {
                  setDowngradeOngoing(true);
                  await downgradeToFreePlan();
                  setDowngradeOngoing(false);
                  dispatch(
                    enqueueSuccess(
                      `Cancelling subscription at the end of the current billing period${currentPeriodEnd ? `: ${currentPeriodEnd.toLocaleDateString()}` : ''}.`,
                      true,
                    ),
                  );
                }}
              >
                Cancel Subscription
              </Button>
            )}
          {contactParagonSales}
        </>
      );
    }
  } else if (canUpgradeToThisPlan) {
    planActionArea = (
      <>
        {startFreeTrial && (
          <Button
            variant="contained"
            sx={{ backgroundColor: plan.color, color: 'white', width: '100%', mt: 2 }}
            onClick={async () => {
              setUpgradeOngoing(true);
              await startFreeTrial();
              setUpgradeOngoing(false);
            }}
          >
            Start 30 Day Free Trial
          </Button>
        )}
        {upgradeToThisPlan && (
          <Button
            variant="contained"
            sx={{ backgroundColor: plan.color, color: 'white', width: '100%', mt: 2 }}
            loading={upgradeOngoing}
            onClick={async () => {
              setUpgradeOngoing(true);
              await upgradeToThisPlan();
              setUpgradeOngoing(false);
            }}
          >
            {ongoingFreeTrial ? 'Pay to Continue' : 'Upgrade'}
          </Button>
        )}
      </>
    );
  }

  return (
    <Card variant={active ? 'outlined' : undefined} sx={active ? { borderColor: plan.color } : undefined}>
      <CardContent>
        <Typography variant="h4" sx={{ color: plan.color }}>
          {plan.name}
        </Typography>

        <Typography>{plan.description}</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Typography variant="h4">
            <strong>${billingPeriod === 'month' ? plan.monthlyPrice : plan.yearlyPrice}</strong>
          </Typography>
          {displayBillingPeriod !== false && <Typography sx={{ ml: 1 }}>per {billingPeriod}</Typography>}
        </Box>

        <List dense sx={{ py: 0 }}>
          {plan.features.map(([feature, color]) => (
            <PlanCardFeatureItem key={feature} text={feature} color={color} />
          ))}
        </List>

        {ongoingFreeTrial && freeTrialEndDate && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Free trial ends on {formatSubscriptionDate(freeTrialEndDate)}
          </Alert>
        )}
        {planActionArea}
      </CardContent>
    </Card>
  );
}
